import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
    EditorState,
    RichUtils,
    ContentState,
    ContentBlock,
    DraftHandleValue,
    Modifier,
    CharacterMetadata,
} from 'draft-js'
import Editor from '@draft-js-plugins/editor'
import { stateToHTML } from 'draft-js-export-html'
import { stateFromHTML } from 'draft-js-import-html'
import {
    Box,
    IconButton,
    Tooltip,
    Divider,
    Select,
    MenuItem,
    FormControl,
} from '@material-ui/core'
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    Title,
    Link as LinkIcon,
    StrikethroughS,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import 'draft-js/dist/Draft.css'

const LINK_DECORATOR_COLOR = '#2563eb'

/** Matches `{{ snake_case }}`-style merge keys from the Insert Variable picker */
const MERGE_VARIABLE_REGEX = /\{\{\s*[a-zA-Z][a-zA-Z0-9_]*\s*\}\}/g

const findMergeVariableRanges = (
    block: ContentBlock,
    callback: (start: number, end: number) => void,
) => {
    const text = block.getText()
    if (!text) return
    const re = new RegExp(MERGE_VARIABLE_REGEX.source, 'g')
    let match = re.exec(text)
    while (match !== null) {
        callback(match.index, match.index + match[0].length)
        match = re.exec(text)
    }
}

const MergeVariableNoSpellcheck: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => (
    <span spellCheck={false} data-gramm="false">
        {children}
    </span>
)

const findLinkEntities = (
    block: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentState,
) => {
    block.findEntityRanges((character) => {
        const entityKey = character.getEntity()
        if (!entityKey) return false
        return contentState.getEntity(entityKey).getType() === 'LINK'
    }, callback)
}

const LinkDecorator: React.FC<{
    entityKey: string
    children: React.ReactNode
    contentState: ContentState
}> = (props) => {
    const entity = props.contentState.getEntity(props.entityKey)
    const data = entity.getData() as { url?: string }
    const url = data?.url ?? '#'

    return (
        <a
            href={url}
            style={{
                color: LINK_DECORATOR_COLOR,
                textDecoration: 'underline',
                cursor: 'pointer',
            }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
                if ((e.ctrlKey || e.metaKey) && url !== '#') {
                    e.preventDefault()
                    window.open(url, '_blank', 'noopener,noreferrer')
                }
            }}
        >
            {props.children}
        </a>
    )
}

/**
 * @draft-js-plugins/editor swaps in its own decorator on mount and only keeps
 * entries from Editor `decorators` / plugin `decorators`; EditorState decorators
 * from createWithContent are ignored. Decorators run in order — later wins on overlap,
 * so the link decorator is last so LINK entities keep anchor styling over merge-var spans.
 */
const emailEditorDecorators = [
    {
        strategy: findMergeVariableRanges,
        component: MergeVariableNoSpellcheck,
    },
    {
        strategy: findLinkEntities,
        component: LinkDecorator,
    },
]

const stripCodeStyle = (contentState: ContentState): ContentState => {
    const blockMap = contentState.getBlockMap()
    const newBlockMap = blockMap.map((block) => {
        if (!block) return block
        const charList = block.getCharacterList()
        const nextCharList = charList.withMutations((mutable) => {
            for (let i = 0; i < mutable.size; i += 1) {
                const char = mutable.get(i)
                if (char && char.getStyle().has('CODE')) {
                    mutable.set(i, CharacterMetadata.removeStyle(char, 'CODE'))
                }
            }
        })
        return block.set('characterList', nextCharList) as ReturnType<
            typeof block.merge
        >
    })
    return contentState.merge({ blockMap: newBlockMap }) as ContentState
}

const FONT_FAMILIES = [
    { label: 'Default', value: '', group: '' },
    // Sans-serif
    {
        label: 'Arial',
        value: 'Arial, Helvetica, sans-serif',
        group: 'Sans-serif',
    },
    {
        label: 'Helvetica',
        value: 'Helvetica, Arial, sans-serif',
        group: 'Sans-serif',
    },
    {
        label: 'Verdana',
        value: 'Verdana, Geneva, sans-serif',
        group: 'Sans-serif',
    },
    {
        label: 'Tahoma',
        value: 'Tahoma, Geneva, sans-serif',
        group: 'Sans-serif',
    },
    {
        label: 'Trebuchet MS',
        value: "'Trebuchet MS', sans-serif",
        group: 'Sans-serif',
    },
    // Serif
    { label: 'Georgia', value: 'Georgia, serif', group: 'Serif' },
    {
        label: 'Times New Roman',
        value: "'Times New Roman', Times, serif",
        group: 'Serif',
    },
    {
        label: 'Palatino',
        value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
        group: 'Serif',
    },
    // Monospace
    {
        label: 'Courier New',
        value: "'Courier New', Courier, monospace",
        group: 'Monospace',
    },
]

const FONT_SIZES = [
    { label: 'Default', value: '' },
    { label: '10px', value: '10px' },
    { label: '12px', value: '12px' },
    { label: '14px', value: '14px' },
    { label: '16px', value: '16px' },
    { label: '18px', value: '18px' },
    { label: '20px', value: '20px' },
    { label: '24px', value: '24px' },
    { label: '28px', value: '28px' },
    { label: '32px', value: '32px' },
]

const FONT_FAMILY_PREFIX = 'FONT_FAMILY_'
const FONT_SIZE_PREFIX = 'FONT_SIZE_'

const customStyleMap: Record<string, React.CSSProperties> = {}

FONT_FAMILIES.forEach(({ value }) => {
    if (value) {
        customStyleMap[`${FONT_FAMILY_PREFIX}${value}`] = { fontFamily: value }
    }
})

FONT_SIZES.forEach(({ value }) => {
    if (value) {
        customStyleMap[`${FONT_SIZE_PREFIX}${value}`] = { fontSize: value }
    }
})

const stateToHTMLOptions = {
    inlineStyles: {
        CODE: { element: 'span' },
    },
    inlineStyleFn: (styles: any) => {
        const cssStyle: Record<string, string> = {}
        const styleArr: string[] = styles.toArray
            ? styles.toArray()
            : Array.from(styles)

        styleArr.forEach((s: string) => {
            if (s.startsWith(FONT_FAMILY_PREFIX)) {
                cssStyle.fontFamily = s.replace(FONT_FAMILY_PREFIX, '')
            } else if (s.startsWith(FONT_SIZE_PREFIX)) {
                cssStyle.fontSize = s.replace(FONT_SIZE_PREFIX, '')
            }
        })

        if (Object.keys(cssStyle).length > 0) {
            return { element: 'span', style: cssStyle }
        }
        return undefined
    },
} as any

const useStyles = makeStyles((theme: Theme) => ({
    editorWrapper: {
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        overflow: 'hidden',
        '&:hover': {
            borderColor: '#3b82f6',
        },
        '&:focus-within': {
            borderColor: '#3b82f6',
            borderWidth: 2,
        },
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: theme.spacing(0.5),
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        gap: 2,
    },
    toolbarButton: {
        padding: 6,
        borderRadius: 4,
        color: '#6b7280',
        '&:hover': {
            backgroundColor: '#e2e8f0',
            color: '#1f2937',
        },
    },
    toolbarButtonActive: {
        padding: 6,
        borderRadius: 4,
        backgroundColor: '#dbeafe',
        color: '#3b82f6',
        '&:hover': {
            backgroundColor: '#bfdbfe',
            color: '#2563eb',
        },
    },
    toolbarSelect: {
        '& .MuiSelect-select': {
            padding: '4px 24px 4px 8px',
            fontSize: '0.75rem',
            minWidth: 80,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e2e8f0',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3b82f6',
        },
        '& .MuiSelect-icon': {
            fontSize: '1rem',
        },
    },
    toolbarMenuItem: {
        fontSize: '0.8rem',
        padding: theme.spacing(0.5, 1.5),
    },
    editorContainer: {
        padding: theme.spacing(1.5, 2),
        minHeight: 200,
        cursor: 'text',
        fontSize: '0.875rem',
        lineHeight: 1.6,
        '& .DraftEditor-root': {
            minHeight: 180,
        },
        '& .public-DraftEditorPlaceholder-root': {
            color: '#9ca3af',
        },
        '& blockquote': {
            borderLeft: '3px solid #3b82f6',
            margin: '8px 0',
            padding: '4px 12px',
            color: '#4b5563',
            backgroundColor: '#f8fafc',
        },
        '& h1, & h2, & h3': {
            margin: '8px 0 4px',
        },
        '& ul, & ol': {
            margin: '4px 0',
            paddingLeft: 24,
        },
        '& pre': {
            backgroundColor: '#f1f5f9',
            padding: '8px 12px',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
        },
        '& .public-DraftEditor-content a': {
            color: LINK_DECORATOR_COLOR,
            textDecoration: 'underline',
        },
    },
    label: {
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6b7280',
        marginBottom: 4,
    },
}))

interface InlineStyle {
    label: string
    style: string
    icon: React.ReactElement
}

const INLINE_STYLES: InlineStyle[] = [
    { label: 'Bold', style: 'BOLD', icon: <FormatBold fontSize="small" /> },
    {
        label: 'Italic',
        style: 'ITALIC',
        icon: <FormatItalic fontSize="small" />,
    },
    {
        label: 'Underline',
        style: 'UNDERLINE',
        icon: <FormatUnderlined fontSize="small" />,
    },
    {
        label: 'Strikethrough',
        style: 'STRIKETHROUGH',
        icon: <StrikethroughS fontSize="small" />,
    },
]

interface BlockType {
    label: string
    style: string
    icon: React.ReactElement
}

const BLOCK_TYPES: BlockType[] = [
    { label: 'Heading', style: 'header-two', icon: <Title fontSize="small" /> },
    {
        label: 'Blockquote',
        style: 'blockquote',
        icon: <FormatQuote fontSize="small" />,
    },
    {
        label: 'Bullet List',
        style: 'unordered-list-item',
        icon: <FormatListBulleted fontSize="small" />,
    },
    {
        label: 'Numbered List',
        style: 'ordered-list-item',
        icon: <FormatListNumbered fontSize="small" />,
    },
]

export interface RichTextEmailEditorHandle {
    insertText: (text: string) => void
}

interface RichTextEmailEditorProps {
    value?: string
    htmlValue?: string
    onChange: (plainText: string, html: string) => void
    placeholder?: string
    label?: string
    toolbarExtra?: React.ReactNode
    onInsertRef?: (insertFn: (text: string) => void) => void
}

const getActiveCustomStyle = (
    editorState: EditorState,
    prefix: string,
): string => {
    const currentStyle = editorState.getCurrentInlineStyle()
    const activeStyle = currentStyle.find(
        (s) => s !== undefined && s.startsWith(prefix),
    )
    return activeStyle ? activeStyle.replace(prefix, '') : ''
}

const applyCustomStyle = (
    editorState: EditorState,
    prefix: string,
    value: string,
): EditorState => {
    const selection = editorState.getSelection()
    const currentStyle = editorState.getCurrentInlineStyle()

    let nextContentState = editorState.getCurrentContent()
    currentStyle.forEach((style) => {
        if (style && style.startsWith(prefix)) {
            nextContentState = Modifier.removeInlineStyle(
                nextContentState,
                selection,
                style,
            )
        }
    })

    let nextEditorState = EditorState.push(
        editorState,
        nextContentState,
        'change-inline-style',
    )

    if (value) {
        const styleName = `${prefix}${value}`
        nextContentState = Modifier.applyInlineStyle(
            nextEditorState.getCurrentContent(),
            selection,
            styleName,
        )
        nextEditorState = EditorState.push(
            nextEditorState,
            nextContentState,
            'change-inline-style',
        )
    }

    return nextEditorState
}

export const RichTextEmailEditor: React.FC<RichTextEmailEditorProps> = ({
    value,
    htmlValue,
    onChange,
    placeholder = 'Type in your follow-up or generate it with AI',
    label,
    toolbarExtra,
    onInsertRef,
}) => {
    const classes = useStyles()
    const editorRef = useRef<Editor>(null)
    const [editorState, setEditorState] = useState<EditorState>(() => {
        if (htmlValue) {
            const contentState = stripCodeStyle(stateFromHTML(htmlValue))
            return EditorState.createWithContent(contentState)
        }
        if (value) {
            const contentState = ContentState.createFromText(
                value.replace(/\\n/g, '\n'),
            )
            return EditorState.createWithContent(contentState)
        }
        return EditorState.createEmpty()
    })

    const initializedRef = useRef(false)

    useEffect(() => {
        if (initializedRef.current) return
        initializedRef.current = true
    }, [])

    const handleEditorChange = useCallback(
        (newState: EditorState) => {
            setEditorState(newState)
            const contentState = newState.getCurrentContent()
            const plainText = contentState.getPlainText()
            const html = stateToHTML(contentState, stateToHTMLOptions)
            onChange(plainText, html)
        },
        [onChange],
    )

    const handleKeyCommand = useCallback(
        (command: string, state: EditorState): DraftHandleValue => {
            if (command === 'code') return 'handled'
            const newState = RichUtils.handleKeyCommand(state, command)
            if (newState) {
                handleEditorChange(newState)
                return 'handled'
            }
            return 'not-handled'
        },
        [handleEditorChange],
    )

    const toggleInlineStyle = useCallback(
        (style: string) => {
            handleEditorChange(RichUtils.toggleInlineStyle(editorState, style))
        },
        [editorState, handleEditorChange],
    )

    const toggleBlockType = useCallback(
        (blockType: string) => {
            handleEditorChange(
                RichUtils.toggleBlockType(editorState, blockType),
            )
        },
        [editorState, handleEditorChange],
    )

    const handleFontFamilyChange = useCallback(
        (value: string) => {
            handleEditorChange(
                applyCustomStyle(editorState, FONT_FAMILY_PREFIX, value),
            )
        },
        [editorState, handleEditorChange],
    )

    const handleFontSizeChange = useCallback(
        (value: string) => {
            handleEditorChange(
                applyCustomStyle(editorState, FONT_SIZE_PREFIX, value),
            )
        },
        [editorState, handleEditorChange],
    )

    const handleAddLink = useCallback(() => {
        const selection = editorState.getSelection()
        if (selection.isCollapsed()) return

        const url = window.prompt('Enter URL:')
        if (!url) return

        const contentState = editorState.getCurrentContent()
        const contentStateWithEntity = contentState.createEntity(
            'LINK',
            'MUTABLE',
            { url },
        )
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
        const newEditorState = EditorState.set(editorState, {
            currentContent: contentStateWithEntity,
        })
        handleEditorChange(
            RichUtils.toggleLink(newEditorState, selection, entityKey),
        )
    }, [editorState, handleEditorChange])

    const focusEditor = useCallback(() => {
        editorRef.current?.focus()
    }, [])

    const currentInlineStyle = editorState.getCurrentInlineStyle()
    const selection = editorState.getSelection()
    const currentBlockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType()

    const activeFontFamily = getActiveCustomStyle(
        editorState,
        FONT_FAMILY_PREFIX,
    )
    const activeFontSize = getActiveCustomStyle(editorState, FONT_SIZE_PREFIX)

    const insertTextAtCursor = useCallback(
        (text: string) => {
            const contentState = editorState.getCurrentContent()
            const selectionState = editorState.getSelection()
            const newContentState = Modifier.insertText(
                contentState,
                selectionState,
                text,
            )
            const newEditorState = EditorState.push(
                editorState,
                newContentState,
                'insert-characters',
            )
            handleEditorChange(newEditorState)
        },
        [editorState, handleEditorChange],
    )

    useEffect(() => {
        if (onInsertRef) {
            onInsertRef(insertTextAtCursor)
        }
    }, [onInsertRef, insertTextAtCursor])

    return (
        <Box className={classes.editorWrapper}>
            <Box className={classes.toolbar}>
                {/* Font Family Select */}
                <Tooltip title="Font" arrow>
                    <FormControl size="small" variant="outlined">
                        <Select
                            value={activeFontFamily}
                            onChange={(e) =>
                                handleFontFamilyChange(e.target.value as string)
                            }
                            displayEmpty
                            className={classes.toolbarSelect}
                            onMouseDown={(e) => e.preventDefault()}
                            renderValue={(val) => {
                                if (!val) return 'Font'
                                const match = FONT_FAMILIES.find(
                                    (f) => f.value === val,
                                )
                                return match ? match.label : 'Font'
                            }}
                        >
                            {FONT_FAMILIES.map((font) => (
                                <MenuItem
                                    key={font.value}
                                    value={font.value}
                                    className={classes.toolbarMenuItem}
                                    style={
                                        font.value
                                            ? { fontFamily: font.value }
                                            : undefined
                                    }
                                >
                                    {font.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Tooltip>

                {/* Font Size Select */}
                <Tooltip title="Font Size" arrow>
                    <FormControl size="small" variant="outlined">
                        <Select
                            value={activeFontSize}
                            onChange={(e) =>
                                handleFontSizeChange(e.target.value as string)
                            }
                            displayEmpty
                            className={classes.toolbarSelect}
                            onMouseDown={(e) => e.preventDefault()}
                            renderValue={(val) => {
                                if (!val) return 'Size'
                                return val as string
                            }}
                        >
                            {FONT_SIZES.map((size) => (
                                <MenuItem
                                    key={size.value}
                                    value={size.value}
                                    className={classes.toolbarMenuItem}
                                >
                                    {size.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Tooltip>

                <Divider
                    orientation="vertical"
                    flexItem
                    style={{ margin: '4px 4px' }}
                />

                {INLINE_STYLES.map((styleObj) => (
                    <Tooltip key={styleObj.style} title={styleObj.label} arrow>
                        <IconButton
                            className={
                                currentInlineStyle.has(styleObj.style)
                                    ? classes.toolbarButtonActive
                                    : classes.toolbarButton
                            }
                            onMouseDown={(e) => {
                                e.preventDefault()
                                toggleInlineStyle(styleObj.style)
                            }}
                            size="small"
                        >
                            {styleObj.icon}
                        </IconButton>
                    </Tooltip>
                ))}

                <Divider
                    orientation="vertical"
                    flexItem
                    style={{ margin: '4px 4px' }}
                />

                {BLOCK_TYPES.map((blockObj) => (
                    <Tooltip key={blockObj.style} title={blockObj.label} arrow>
                        <IconButton
                            className={
                                currentBlockType === blockObj.style
                                    ? classes.toolbarButtonActive
                                    : classes.toolbarButton
                            }
                            onMouseDown={(e) => {
                                e.preventDefault()
                                toggleBlockType(blockObj.style)
                            }}
                            size="small"
                        >
                            {blockObj.icon}
                        </IconButton>
                    </Tooltip>
                ))}

                <Tooltip title="Insert Link" arrow>
                    <IconButton
                        className={classes.toolbarButton}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            handleAddLink()
                        }}
                        size="small"
                    >
                        <LinkIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {toolbarExtra && (
                    <>
                        <Divider
                            orientation="vertical"
                            flexItem
                            style={{ margin: '4px 4px' }}
                        />
                        {toolbarExtra}
                    </>
                )}
            </Box>

            <Box className={classes.editorContainer} onClick={focusEditor}>
                <Editor
                    ref={editorRef}
                    decorators={emailEditorDecorators}
                    editorState={editorState}
                    onChange={handleEditorChange}
                    handleKeyCommand={handleKeyCommand}
                    customStyleMap={customStyleMap}
                    placeholder={placeholder}
                    spellCheck
                />
            </Box>
        </Box>
    )
}

export default RichTextEmailEditor
